package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.RhOvertime
import br.com.vvrefeicoes.portal.domain.model.RhOvertimeGroup
import br.com.vvrefeicoes.portal.domain.model.RhOvertimeMonth
import br.com.vvrefeicoes.portal.domain.model.RhRanking
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Scale
import com.composables.icons.lucide.TrendingUp
import com.composables.icons.lucide.Users
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_col_cost_allowance
import portalvv.shared.generated.resources.rh_col_employees
import portalvv.shared.generated.resources.rh_col_hazardous
import portalvv.shared.generated.resources.rh_col_insalubrity
import portalvv.shared.generated.resources.rh_col_night_shift
import portalvv.shared.generated.resources.rh_col_salary_difference
import portalvv.shared.generated.resources.rh_col_total
import portalvv.shared.generated.resources.rh_col_unit
import portalvv.shared.generated.resources.rh_legend_bonus
import portalvv.shared.generated.resources.rh_legend_productivity
import portalvv.shared.generated.resources.rh_overtime_100
import portalvv.shared.generated.resources.rh_overtime_50
import portalvv.shared.generated.resources.rh_overtime_average
import portalvv.shared.generated.resources.rh_overtime_by_job
import portalvv.shared.generated.resources.rh_overtime_by_manager
import portalvv.shared.generated.resources.rh_overtime_chart_additionals
import portalvv.shared.generated.resources.rh_overtime_chart_composition
import portalvv.shared.generated.resources.rh_overtime_chart_employees
import portalvv.shared.generated.resources.rh_overtime_chart_other
import portalvv.shared.generated.resources.rh_overtime_chart_total
import portalvv.shared.generated.resources.rh_overtime_col_item
import portalvv.shared.generated.resources.rh_overtime_col_share
import portalvv.shared.generated.resources.rh_overtime_col_value
import portalvv.shared.generated.resources.rh_overtime_col_with_overtime
import portalvv.shared.generated.resources.rh_overtime_dsr
import portalvv.shared.generated.resources.rh_overtime_employees
import portalvv.shared.generated.resources.rh_overtime_employees_hint
import portalvv.shared.generated.resources.rh_overtime_empty
import portalvv.shared.generated.resources.rh_overtime_month_breakdown
import portalvv.shared.generated.resources.rh_overtime_share
import portalvv.shared.generated.resources.rh_overtime_total
import portalvv.shared.generated.resources.rh_overtime_units_title
import kotlin.math.roundToInt

private val KPI_MIN_WIDTH = 240.dp
private val CHART_MIN_WIDTH = 430.dp
private val CARD_MIN_WIDTH = 360.dp
private val UNIT_NAME_WIDTH = 240.dp
private val UNIT_VALUE_WIDTH = 132.dp
private val BREAKDOWN_VALUE_WIDTH = 132.dp
private val BREAKDOWN_SHARE_WIDTH = 96.dp
private const val UNITS_PAGE_SIZE = 15

private val OVERTIME_50_COLOR = AppPalette.Warning500
private val OVERTIME_100_COLOR = AppPalette.Error500
private val OVERTIME_DSR_COLOR = AppPalette.Primary500
private val TOTAL_COLOR = AppPalette.Warning900
private val EMPLOYEES_COLOR = AppPalette.Primary500
private val NIGHT_SHIFT_COLOR = AppPalette.Primary900
private val INSALUBRITY_COLOR = AppPalette.Info500
private val HAZARDOUS_COLOR = AppPalette.Error500
private val BONUS_COLOR = AppPalette.Accent500
private val PRODUCTIVITY_COLOR = AppPalette.Info500
private val COST_ALLOWANCE_COLOR = AppPalette.Success500
private val SALARY_DIFFERENCE_COLOR = AppPalette.Neutral500

@Composable
fun RhOvertimeContent(overtime: RhOvertime) {
    val current = overtime.current
    if (current == null) {
        EmptyState(message = stringResource(Res.string.rh_overtime_empty))
        return
    }
    RhOvertimeKpis(current)
    RhOvertimeCharts(overtime.monthly)
    RhOvertimeBreakdown(current)
    RhOvertimeUnitsTable(overtime.byUnit)
    RhGrid(
        minColumnWidth = CARD_MIN_WIDTH,
        maxColumns = 2,
        items =
            listOf(
                { modifier ->
                    RhRankingCard(
                        title = stringResource(Res.string.rh_overtime_by_job),
                        ranking = overtime.byJobTitle.toRanking(),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhRankingCard(
                        title = stringResource(Res.string.rh_overtime_by_manager),
                        ranking = overtime.byManager.toRanking(),
                        modifier = modifier,
                    )
                },
            ),
    )
}

/** Só entram no ranking os grupos que tiveram hora extra; a contagem é de quem recebeu. */
private fun List<RhOvertimeGroup>.toRanking(): List<RhRanking> =
    filter { it.overtimeTotal > 0.0 }
        .map { RhRanking(name = it.name, total = it.overtimeTotal, employees = it.employeesWithOvertime) }

@Composable
private fun RhOvertimeKpis(current: RhOvertimeMonth) {
    RhGrid(
        minColumnWidth = KPI_MIN_WIDTH,
        maxColumns = 4,
        items =
            listOf(
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.TrendingUp,
                        iconColor = AppPalette.Warning500,
                        iconContainerColor = AppPalette.Warning100,
                        label = stringResource(Res.string.rh_overtime_total),
                        value = rhMoney(current.overtimeTotal),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Banknote,
                        iconColor = AppPalette.Warning500,
                        iconContainerColor = AppPalette.Warning100,
                        label = stringResource(Res.string.rh_overtime_50),
                        value = rhMoney(current.overtime50),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Banknote,
                        iconColor = AppPalette.Error500,
                        iconContainerColor = AppPalette.Error100,
                        label = stringResource(Res.string.rh_overtime_100),
                        value = rhMoney(current.overtime100),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Banknote,
                        iconColor = AppPalette.Primary500,
                        iconContainerColor = AppPalette.Primary100,
                        label = stringResource(Res.string.rh_overtime_dsr),
                        value = rhMoney(current.overtimeDsr),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Users,
                        iconColor = AppPalette.Primary500,
                        iconContainerColor = AppPalette.Primary100,
                        label = stringResource(Res.string.rh_overtime_employees),
                        value = current.employeesWithOvertime.toString(),
                        hint = stringResource(Res.string.rh_overtime_employees_hint, current.employees),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Users,
                        iconColor = AppPalette.Success500,
                        iconContainerColor = AppPalette.Success100,
                        label = stringResource(Res.string.rh_overtime_average),
                        value = rhMoney(current.averagePerEmployee),
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhKpiTile(
                        icon = Lucide.Scale,
                        iconColor = AppPalette.Info900,
                        iconContainerColor = AppPalette.Info100,
                        label = stringResource(Res.string.rh_overtime_share),
                        value = rhPercent(current.overtimeSharePercent),
                        modifier = modifier,
                    )
                },
            ),
    )
}

@Composable
private fun RhOvertimeCharts(monthly: List<RhOvertimeMonth>) {
    val labels = monthly.map { MonthCalendar.shortLabel(it.period) }
    val moneyLabel: (Float) -> String = { value -> rhMoneyCompact(value.toDouble()) }
    val countLabel: (Float) -> String = { value -> value.roundToInt().toString() }
    RhGrid(
        minColumnWidth = CHART_MIN_WIDTH,
        maxColumns = 2,
        items =
            listOf(
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_overtime_chart_composition),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_overtime_50),
                                    color = OVERTIME_50_COLOR,
                                    values = monthly.map { it.overtime50.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_overtime_100),
                                    color = OVERTIME_100_COLOR,
                                    values = monthly.map { it.overtime100.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_overtime_dsr),
                                    color = OVERTIME_DSR_COLOR,
                                    values = monthly.map { it.overtimeDsr.toFloat() },
                                ),
                            ),
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhLineCard(
                        title = stringResource(Res.string.rh_overtime_chart_total),
                        legend = stringResource(Res.string.rh_overtime_total),
                        labels = labels,
                        values = monthly.map { it.overtimeTotal.toFloat() },
                        color = TOTAL_COLOR,
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhLineCard(
                        title = stringResource(Res.string.rh_overtime_chart_employees),
                        legend = stringResource(Res.string.rh_overtime_employees),
                        labels = labels,
                        values = monthly.map { it.employeesWithOvertime.toFloat() },
                        color = EMPLOYEES_COLOR,
                        format = countLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_overtime_chart_additionals),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_col_night_shift),
                                    color = NIGHT_SHIFT_COLOR,
                                    values = monthly.map { it.nightShift.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_col_insalubrity),
                                    color = INSALUBRITY_COLOR,
                                    values = monthly.map { it.insalubrity.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_col_hazardous),
                                    color = HAZARDOUS_COLOR,
                                    values = monthly.map { it.hazardousDuty.toFloat() },
                                ),
                            ),
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
                { modifier ->
                    RhGroupedBarCard(
                        title = stringResource(Res.string.rh_overtime_chart_other),
                        labels = labels,
                        series =
                            listOf(
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_legend_bonus),
                                    color = BONUS_COLOR,
                                    values = monthly.map { it.bonus.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_legend_productivity),
                                    color = PRODUCTIVITY_COLOR,
                                    values = monthly.map { it.productivity.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_col_cost_allowance),
                                    color = COST_ALLOWANCE_COLOR,
                                    values = monthly.map { it.costAllowance.toFloat() },
                                ),
                                RhChartSeries(
                                    label = stringResource(Res.string.rh_col_salary_difference),
                                    color = SALARY_DIFFERENCE_COLOR,
                                    values = monthly.map { it.salaryDifference.toFloat() },
                                ),
                            ),
                        format = moneyLabel,
                        modifier = modifier,
                    )
                },
            ),
    )
}

/** Cada verba variável do mês, com o valor e a fatia no total dos custos variáveis. */
@Composable
private fun RhOvertimeBreakdown(current: RhOvertimeMonth) {
    val items =
        listOf(
            stringResource(Res.string.rh_overtime_total) to current.overtimeTotal,
            stringResource(Res.string.rh_legend_productivity) to current.productivity,
            stringResource(Res.string.rh_col_night_shift) to current.nightShift,
            stringResource(Res.string.rh_legend_bonus) to current.bonus,
            stringResource(Res.string.rh_col_cost_allowance) to current.costAllowance,
            stringResource(Res.string.rh_col_salary_difference) to current.salaryDifference,
            stringResource(Res.string.rh_col_insalubrity) to current.insalubrity,
            stringResource(Res.string.rh_col_hazardous) to current.hazardousDuty,
        ).sortedByDescending { it.second }
    ChartCard(
        title = stringResource(Res.string.rh_overtime_month_breakdown),
        modifier = Modifier.fillMaxWidth(),
    ) {
        RhBreakdownRow(
            name = stringResource(Res.string.rh_overtime_col_item),
            value = stringResource(Res.string.rh_overtime_col_value),
            share = stringResource(Res.string.rh_overtime_col_share),
            header = true,
        )
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        items.forEach { (name, value) ->
            RhBreakdownRow(
                name = name,
                value = rhMoney(value),
                share = if (current.totalCost > 0.0) rhPercent(value / current.totalCost * 100) else RH_EMPTY_VALUE,
            )
        }
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        RhBreakdownRow(
            name = stringResource(Res.string.rh_col_total),
            value = rhMoney(current.totalCost),
            share = if (current.totalCost > 0.0) rhPercent(100.0) else RH_EMPTY_VALUE,
            emphasized = true,
        )
    }
}

@Composable
private fun RhBreakdownRow(
    name: String,
    value: String,
    share: String,
    header: Boolean = false,
    emphasized: Boolean = false,
) {
    val style = if (header) MaterialTheme.typography.labelMedium else MaterialTheme.typography.bodyMedium
    val color = if (header) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.onSurface
    val weight = if (emphasized) FontWeight.SemiBold else null
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = name,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = value,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            textAlign = TextAlign.End,
            modifier = Modifier.width(BREAKDOWN_VALUE_WIDTH),
        )
        Text(
            text = share,
            style = style,
            color = color,
            fontWeight = weight,
            maxLines = 1,
            textAlign = TextAlign.End,
            modifier = Modifier.width(BREAKDOWN_SHARE_WIDTH),
        )
    }
}

private class RhOvertimeUnitColumn(
    val title: String,
    val value: (RhOvertimeGroup) -> String,
)

@Composable
private fun RhOvertimeUnitsTable(units: List<RhOvertimeGroup>) {
    val columns =
        listOf(
            RhOvertimeUnitColumn(stringResource(Res.string.rh_col_employees)) { it.employees.toString() },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_col_with_overtime)) { it.employeesWithOvertime.toString() },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_50)) { rhMoney(it.overtime50) },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_100)) { rhMoney(it.overtime100) },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_dsr)) { rhMoney(it.overtimeDsr) },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_total)) { rhMoney(it.overtimeTotal) },
            RhOvertimeUnitColumn(stringResource(Res.string.rh_overtime_col_share)) { rhPercent(it.sharePercent) },
        )
    var page by remember(units) { mutableStateOf(0) }
    val pageCount = (units.size + UNITS_PAGE_SIZE - 1) / UNITS_PAGE_SIZE
    val visible = units.drop(page * UNITS_PAGE_SIZE).take(UNITS_PAGE_SIZE)
    val scrollState = rememberScrollState()

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = stringResource(Res.string.rh_overtime_units_title),
                style = MaterialTheme.typography.titleMedium,
            )
            Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
                Column {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        RhTableCell(
                            text = stringResource(Res.string.rh_col_unit),
                            width = UNIT_NAME_WIDTH,
                            header = true,
                            align = TextAlign.Start,
                        )
                        columns.forEach { column ->
                            RhTableCell(text = column.title, width = UNIT_VALUE_WIDTH, header = true)
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    visible.forEach { unit ->
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            RhTableCell(text = unit.name, width = UNIT_NAME_WIDTH, align = TextAlign.Start)
                            columns.forEach { column ->
                                RhTableCell(text = column.value(unit), width = UNIT_VALUE_WIDTH)
                            }
                        }
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
            AppHorizontalScrollbar(
                state = scrollState,
                modifier = Modifier.fillMaxWidth(),
            )
            AppPagination(
                page = page,
                pageCount = pageCount,
                onPageChange = { page = it.coerceIn(0, (pageCount - 1).coerceAtLeast(0)) },
                modifier = Modifier.align(Alignment.End),
            )
        }
    }
}
